import { memo } from 'react';
import styled from '@emotion/styled';
import AuraLogo from '@trinity/chat-components/static/images/aura-icon.svg?url';

export type DigitalTwinSegment = {
  id: number;
  identifier: string;
  name: string;
  subtitle?: string | null;
  description?: string | null;
  iconUrl?: string | null;
};

type Props = {
  segments: DigitalTwinSegment[];
  onChatWithSegment: (segment: DigitalTwinSegment) => void;
  onViewSegmentProfile: (segment: DigitalTwinSegment) => void;
  backLink?: React.ReactNode;
};

export const DigitalTwinsGallery = memo(
  ({ segments, onChatWithSegment, onViewSegmentProfile, backLink }: Props) => {
    return (
      <Root>
        {backLink && <BackLinkWrapper>{backLink}</BackLinkWrapper>}
        <Title>Digital Twins</Title>
        <CardGrid>
          {segments.map((segment) => (
            <SegmentCard
              key={segment.identifier}
              segment={segment}
              onChatWithSegment={onChatWithSegment}
              onViewSegmentProfile={onViewSegmentProfile}
            />
          ))}
        </CardGrid>
      </Root>
    );
  }
);

DigitalTwinsGallery.displayName = 'DigitalTwinsGallery';

type CardProps = {
  segment: DigitalTwinSegment;
  onChatWithSegment: (segment: DigitalTwinSegment) => void;
  onViewSegmentProfile: (segment: DigitalTwinSegment) => void;
};

const SegmentCard = memo(({ segment, onChatWithSegment, onViewSegmentProfile }: CardProps) => {
  return (
    <CardRoot onClick={() => onViewSegmentProfile(segment)}>
      <CardTop>
        <AvatarWrapper>
          {segment.iconUrl ? (
            <AvatarImage src={segment.iconUrl} alt={segment.name} />
          ) : (
            <AvatarFallback>{segment.name.charAt(0).toUpperCase()}</AvatarFallback>
          )}
        </AvatarWrapper>
        <CardTextGroup>
          <SegmentName>{segment.name}</SegmentName>
          {segment.subtitle && <SegmentSubtitle>{segment.subtitle}</SegmentSubtitle>}
        </CardTextGroup>
      </CardTop>
      {segment.description && <SegmentDescription>{segment.description}</SegmentDescription>}
      <CardActions>
        <ChatWithSegmentButton
          onClick={(e) => {
            e.stopPropagation();
            onChatWithSegment(segment);
          }}
        >
          <img src={AuraLogo} alt="" aria-hidden="true" />
          Chat with Segment
        </ChatWithSegmentButton>
      </CardActions>
    </CardRoot>
  );
});

SegmentCard.displayName = 'SegmentCard';

const BackLinkWrapper = styled.div({
  alignSelf: 'flex-start',
  marginBottom: 8,
});

const Root = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  overflowY: 'auto',
  padding: '32px 24px',
  boxSizing: 'border-box',
});

const Title = styled.h2(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 28,
  color: theme.palette.black?.main ?? '#000',
  marginBottom: 32,
  textAlign: 'center',
}));

const CardGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 20,
  width: '100%',
  maxWidth: 960,
  '@media (max-width: 700px)': {
    gridTemplateColumns: '1fr',
  },
});

const CardRoot = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.white.main,
  borderTop: `1px solid ${theme.palette.gray.light4}`,
  borderRight: `1px solid ${theme.palette.gray.light4}`,
  borderBottom: `1px solid ${theme.palette.gray.light4}`,
  borderLeft: `4px solid ${
    theme.isTrinityThemed ? theme.palette.sentiment.primary.main : theme.palette.primary.main
  }`,
  borderRadius: 10,
  padding: '16px 20px',
  minHeight: 200,
  boxSizing: 'border-box',
  gap: 14,
  cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  transition: 'box-shadow 0.15s ease',
  '&:hover': {
    boxShadow: '0 6px 18px rgba(0,0,0,0.13)',
  },
}));

const CardTop = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: 14,
});

const AvatarWrapper = styled.div(({ theme }) => ({
  width: 100,
  height: 100,
  borderRadius: '50%',
  padding: 3,
  boxSizing: 'border-box',
  backgroundColor: theme.palette.white.main,
  flexShrink: 0,
  overflow: 'hidden',
}));

const AvatarImage = styled.img({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '50%',
  display: 'block',
});

const AvatarFallback = styled.div(({ theme }) => ({
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.isTrinityThemed
    ? theme.palette.sentiment.primary.light
    : theme.palette.btnPrimary.bg,
  color: theme.isTrinityThemed ? theme.palette.sentiment.primary.main : theme.palette.primary.main,
  fontSize: 24,
  fontFamily: theme.fonts.semiBold,
}));

const CardTextGroup = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
});

const SegmentName = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 18,
  color: theme.isTrinityThemed ? theme.palette.sentiment.primary.main : theme.palette.primary.main,
  textAlign: 'left',
  marginTop: 10,
  marginBottom: 10,
}));

const SegmentSubtitle = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 14,
  color: theme.palette.gray.dark1 ?? theme.palette.gray.main,
  textAlign: 'left',
  marginBottom: 4,
}));

const SegmentDescription = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.regular,
  fontSize: 12,
  color: theme.palette.gray.dark1 ?? theme.palette.gray.main,
  textAlign: 'left',
  lineHeight: 1.5,
  marginTop: 8,
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}));

const CardActions = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  alignItems: 'center',
});

const BaseButton = styled.button({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  width: 'fit-content',
  padding: '8px 16px',
  borderRadius: 8,
  border: 'none',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  boxSizing: 'border-box',
  transition: 'opacity 0.2s ease',
  '&:hover': {
    opacity: 0.85,
  },
  '& img': {
    width: 14,
    height: 14,
    flexShrink: 0,
  },
});

const ChatWithSegmentButton = styled(BaseButton)(({ theme }) => ({
  background: theme.isTrinityThemed
    ? theme.palette.sentiment.primary.main
    : theme.palette.primary.main,
  color: 'white',
}));
